import BaseModal from "components/BaseModal";
import message from "components/CustomMessage";
import { LoadingIcon } from "components/Icons";
import _ from "lodash";
import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

interface AsanaOAuthPopupProps {
  afterClose?: (failedStatus: boolean, loginId: string) => void;
  id?: string;
  [key: string]: any;
}

function AsanaOAuthPopup(
  { afterClose: _afterClose, ...props }: AsanaOAuthPopupProps,
  ref: ForwardedRef<any>,
) {
  const errorRef = useRef(false);
  const failedStatusRef = useRef(true);
  const thisRef = useRef<{ close: () => void } | null>(null);
  //login id is used in Mapping Form Modal once OAuth login is successful
  const loginIdRef = useRef<string | undefined>(undefined);
  const currentOrigin = window.location.origin;
  const URL = `${currentOrigin}/api/v2/tickets/oauth2/ASANA/redirect`;
  useImperativeHandle(ref, () => ({
    ...thisRef,
  }));

  const afterClose = useCallback(() => {
    _afterClose &&
      _afterClose(failedStatusRef.current, loginIdRef.current || "");
  }, [_afterClose]);

  useEffect(() => {
    const _window = window.open(
      URL,
      "_blank",
      "width=1000,height=800,rel=noopener",
    );
    if (_.isNull(_window)) {
      message.error(
        "Pop-up window is blocked on your browser, please enable and retry.",
      );
      afterClose();
      return;
    }
    const timer = setInterval(function () {
      try {
        // _window becomes null if the popup window is blocked by the browser.
        // User closes the OAuth window before access
        if (!_window || _window.closed) {
          clearInterval(timer);
          message.error("Unable to connect to Asana, please try again.");
          thisRef?.current?.close();
          return;
        }
        if (_window.location.pathname.endsWith("install")) {
          const bodyText = _window.document.body.innerText;
          const closeModal = () => {
            _window.close();
            thisRef?.current?.close();
            clearInterval(timer);
          };
          //extract only xml string
          let successVal;
          let idVal;
          const xmlIndex = bodyText.indexOf("<");
          if (xmlIndex !== -1) {
            const bodyXmlOnlyText = bodyText.substring(xmlIndex);
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(
              bodyXmlOnlyText,
              "application/xml",
            );
            if (xmlDoc.getElementsByTagName("parsererror").length > 0) {
              failedStatusRef.current = true;
              closeModal();
            }
            successVal = xmlDoc?.querySelector("data > success")?.textContent;
            idVal = xmlDoc?.querySelector("data > message")?.textContent;
            if (successVal === "true" && idVal) {
              loginIdRef.current = idVal;
              failedStatusRef.current = false;
              closeModal();
              return;
            } else if (errorRef.current) {
              closeModal();
            }
          }
        }
      } catch (e) {
        errorRef.current = true;
      }
    }, 1000);
    return () => {
      clearInterval(timer);
      _window.close();
    };
  }, [afterClose, URL]);

  return (
    <BaseModal
      modalProps={{
        title: "Connecting Asana",
        footer: null,
      }}
      ref={thisRef}
      afterClose={afterClose}
    >
      <div>
        <LoadingIcon /> Waiting for Asana OAuth.
      </div>
    </BaseModal>
  );
}

export default forwardRef(AsanaOAuthPopup);
